import { SessionContext } from '@onwardtravelsolutions/package-react/Contexts';
import {
    Environment,
    NoticeType,
} from '@onwardtravelsolutions/package-react/Enums';
import {
    ResponseNotice,
    Session,
} from '@onwardtravelsolutions/package-react/Types';
import { generateUuid } from '@onwardtravelsolutions/package-react/Utilities/strings';
import { ReactNode, useContext, useEffect } from 'react';
import { Bounce, toast, ToastContainer } from 'react-toastify';
import { Link } from './../Types';
import EnvironmentIndicator from './EnvironmentIndicator';
import Footer from './Footer';
import Header from './Header';
import { ToastNotice } from './Notices';

const defaultLinks: Link[] = [
    {
        href: '/#our-vision',
        title: 'Onward Travel Solutions Vision',
        label: 'Vision',
    },
    {
        href: '/#team-operations',
        title: 'About Onward Travel Solutions',
        label: 'Teams',
    },
    {
        href: '/#technology',
        title: 'Onward Travel Solutions Products & Technologies',
        label: 'Technology',
    },
    {
        href: '/#contact-us',
        title: 'Contact Onward Travel Solutions',
        label: 'Contact',
    },
];

type LayoutProps = {
    children: ReactNode;
    links?: Link[];
    env?: Environment;
    notices?: ResponseNotice[];
};

export default function Layout({
    children,
    links = defaultLinks,
    env = Environment.Production,
    notices = [],
}: LayoutProps) {
    const { globalNotices, removeGlobalNotice, addGlobalNotice } = useContext(
        SessionContext,
    ) as Session;

    useEffect(() => {
        globalNotices.forEach((notice) => {
            toast(
                ({ closeToast, isPaused }) => (
                    <ToastNotice
                        notice={notice}
                        closeToast={closeToast}
                        isPaused={isPaused}
                    />
                ),
                {
                    toastId: notice.uid,
                    onOpen() {
                        removeGlobalNotice(notice.uid);
                    },
                },
            );
        });
    }, [globalNotices]);

    useEffect(() => {
        notices.forEach((notice) => {
            addGlobalNotice(convertRequestNoticeToToast(notice));
        });
    }, [notices]);

    return (
        <div className='overflow-x-hidden'>
            <Header links={links} />
            <article>{children}</article>
            <EnvironmentIndicator env={env} />
            <Footer />
            <ToastContainer
                newestOnTop
                closeOnClick
                pauseOnFocusLoss
                hideProgressBar
                customProgressBar={true}
                draggable
                pauseOnHover
                closeButton={false}
                transition={Bounce}
                toastClassName='bg-transparent! p-0! md:w-md!'
            />
        </div>
    );
}

const convertRequestNoticeToToast = (notice: ResponseNotice) => ({
    title: notice.title,
    body: notice.markdown?.body ?? '',
    type: notice.type as NoticeType,
    uid: notice.meta?.uid ?? generateUuid(),
    ttl: notice.meta?.ttl ?? null,
});
